"use client";

import React, { createContext, useContext, useState, ReactNode } from "react";
import type { Agent, ChatMessage } from "../types/agent.types";

interface AgentContextType {
  agents: Agent[];
  chats: Record<string, ChatMessage[]>;
  addAgent: (agent: Agent) => void;
  updateAgent: (id: string, updates: Partial<Agent>) => void;
  deleteAgent: (id: string) => void;
  addMessage: (agentId: string, message: ChatMessage) => void;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export function AgentProvider({ children }: { children: ReactNode }) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [chats, setChats] = useState<Record<string, ChatMessage[]>>({});

  const addAgent = (agent: Agent) => {
    setAgents((prev) => [...prev, agent]);
    setChats((prev) => ({ ...prev, [agent.id]: [] }));
  };

  const updateAgent = (id: string, updates: Partial<Agent>) => {
    setAgents((prev) =>
      prev.map((agent) => (agent.id === id ? { ...agent, ...updates } : agent))
    );
  };

  const deleteAgent = (id: string) => {
    setAgents((prev) => prev.filter((agent) => agent.id !== id));
    setChats((prev) => {
      const newChats = { ...prev };
      delete newChats[id];
      return newChats;
    });
  };

  const addMessage = (agentId: string, message: ChatMessage) => {
    setChats((prev) => ({
      ...prev,
      [agentId]: [...(prev[agentId] || []), message],
    }));
  };

  return (
    <AgentContext.Provider
      value={{ agents, chats, addAgent, updateAgent, deleteAgent, addMessage }}
    >
      {children}
    </AgentContext.Provider>
  );
}

export function useAgentContext() {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error("useAgentContext must be used within an AgentProvider");
  }
  return context;
}
